import { ASSIGNMENT_MODE, isActive } from './coachingForms';

// Publishing a new version of a block an athlete already has: "Replace with…"
// on the coach's list, POST /assignments/:id/supersede.
//
// The server archives the old assignment, copies the new block for the same
// athlete, and records the version. Everything here is the decision the screen
// has to get right before it sends anything: which rows can be replaced, and
// what a complete replacement looks like.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Whether a row offers "Replace with…".
 *
 * Only an active copy. The server answers 409 for anything else, and a button
 * that can only ever fail is worse than no button:
 *   - a shared calendar is the coach's own programme; editing it already
 *     reaches the squad, and there is no athlete copy to replace;
 *   - an archived assignment is history, and replacing it again would fork the
 *     version line beside the version that already replaced it.
 */
export function canReplace(assignment) {
  return Boolean(
    assignment
    && isActive(assignment)
    && assignment.mode === ASSIGNMENT_MODE.COPY
    && Number(assignment.athleteId)
  );
}

/**
 * The version tag for a row, or '' for a first version.
 *
 * A first version carries no tag: nearly every assignment is one, and "v1" on
 * every row would be noise that hides the rows where it means something. A
 * missing version is a first version — the server reads it the same way.
 */
export function versionLabel(assignment) {
  const version = Number(assignment?.version) || 1;
  return version > 1 ? `v${version}` : '';
}

/** An empty replacement form for one assignment. */
export function emptyReplacement(assignment) {
  return { assignmentId: assignment?.id ?? null, programId: '', startDate: '' };
}

/**
 * Validate a replacement before it is sent.
 *
 * The start date is required, unlike a first assignment's. It is the day the
 * athlete's calendar switches from the old block to the new one; without it the
 * server refuses the request, and the reason is better said here, next to the
 * field, than as an error banner after a round trip.
 */
export function validateReplacement(form) {
  const errors = {};

  if (!Number(form?.programId)) {
    errors.programId = 'Choose the new block.';
  }

  const startDate = String(form?.startDate ?? '').trim();
  if (!startDate) {
    errors.startDate = 'Choose the day the new version starts.';
  } else if (!ISO_DATE.test(startDate)) {
    errors.startDate = 'Use a date like 2026-09-14.';
  }

  return errors;
}

/** The request body for a supersede. */
export function buildSupersedeRequest(form) {
  const body = {
    programId: Number(form?.programId) || 0,
    startDate: String(form?.startDate ?? '').trim(),
  };
  const notes = String(form?.notes ?? '').trim();
  if (notes) body.notes = notes;
  return body;
}

/** The API path a replacement of this assignment posts to. */
export function supersedePath(assignment) {
  return `/assignments/${assignment.id}/supersede`;
}

/**
 * What the coach is told once the new version is published.
 *
 * It says the old block and its log are kept, because "replace" reads as
 * "delete" and the first thing a coach worries about is the athlete's history.
 */
export function replacementNotice(assignment, form, programs) {
  const who = assignment?.athleteName || 'The athlete';
  const program = (programs ?? []).find((p) => Number(p.id) === Number(form?.programId));
  const name = program?.name || 'the new block';
  const next = (Number(assignment?.version) || 1) + 1;
  const startDate = String(form?.startDate ?? '').trim();
  return `${who} moves to version ${next}, ${name}, on ${startDate}. `
    + 'Their old block and everything logged against it are kept.';
}
